import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Activity, Smartphone, RefreshCw } from "lucide-react";
import ActivityRow from "./ActivityRow";
import { groupActivities } from "./activityGroups";
import betaAPI from "v2/api";
import { dateLocale, t as translate } from "i18n";

// "Today", "Yesterday", or e.g. "Monday 22 September".
const dayHeading = ({ kind, date }) => {
  if (kind === "today") return translate("common.time.today");
  if (kind === "yesterday") return translate("common.time.yesterday");
  return date.toLocaleDateString(dateLocale("en-GB"), {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
};

// Skeleton component for loading state
const ActivityRowSkeleton = () => (
  <div className="flex animate-pulse items-center gap-3 border-b border-[#F3F4F6] px-4 py-3">
    <div className="h-7 w-7 rounded-full bg-[#E5E7EB]" />
    <div className="flex-1">
      <div className="h-3 w-48 rounded bg-[#E5E7EB]" />
    </div>
    <div className="h-2.5 w-16 rounded bg-[#E5E7EB]" />
  </div>
);

const RecentActivitySection = ({ className = "" }) => {
  const { t } = useTranslation();
  const { workspaceId } = useParams();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchActivities = async () => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await betaAPI.getWorkspaceActivity({
        workspaceId,
        // Fetch extra so the list stays full after similar events are merged.
        queryParams: { page_size: 25 },
      });

      const activitiesData = response.data?.activities?.results || [];
      setActivities(activitiesData);
    } catch (err) {
      console.error("Failed to fetch workspace activity", err);
      setError(err.message || t("workspace.overview.activity.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, [workspaceId]);

  const handleRetry = () => {
    fetchActivities();
  };

  return (
    <div
      className={`flex flex-col rounded-xl border border-[#F3F4F6] bg-white ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
        <h2 className="text-sm font-semibold text-[#111827]">
          {t("workspace.overview.activity.title")}
        </h2>
      </div>

      {/* Activity list */}
      <div className="flex-1">
        {loading ? (
          // Loading skeleton
          <>
            <ActivityRowSkeleton />
            <ActivityRowSkeleton />
            <ActivityRowSkeleton />
            <ActivityRowSkeleton />
          </>
        ) : error ? (
          // Error state with retry
          <div className="flex flex-col items-center justify-center px-6 py-12">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-[#FEF2F2]">
              <Activity size={24} className="text-[#DC2626]" />
            </div>
            <h3 className="mb-1 text-sm font-semibold text-[#111827]">
              {t("workspace.overview.activity.loadFailed")}
            </h3>
            <p className="mb-4 text-center text-xs text-[#6B7280]">{error}</p>
            <button
              onClick={handleRetry}
              className="flex items-center gap-1.5 rounded-lg border border-[#E5E7EB] px-3 py-1.5 text-xs font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
            >
              <RefreshCw size={12} />
              {t("common.actions.retry")}
            </button>
          </div>
        ) : activities.length === 0 ? (
          // Empty state
          <div className="flex flex-col items-center justify-center px-6 py-12">
            {/* Icon with decorative background */}
            <div className="relative mb-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-[#F0FDF4] to-[#DCFCE7]">
                <Activity size={28} className="text-[#10B981]" />
              </div>
              <div className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-lg border border-[#F3F4F6] bg-white shadow-sm">
                <Smartphone size={12} className="text-[#9CA3AF]" />
              </div>
            </div>

            {/* Text content */}
            <h3 className="mb-1 text-sm font-semibold text-[#111827]">
              {t("workspace.overview.activity.emptyTitle")}
            </h3>
            <p className="max-w-[220px] text-center text-xs leading-relaxed text-[#6B7280]">
              {t("workspace.overview.activity.emptyDescription")}
            </p>
          </div>
        ) : (
          // Activity list, grouped under day headings
          groupActivities(activities).map((day) => (
            <section key={day.key}>
              <h3 className="bg-[#FAFAFA] px-4 py-1.5 text-[11px] font-medium uppercase tracking-wide text-[#9CA3AF]">
                {dayHeading(day)}
              </h3>
              {day.rows.map((row, index) => (
                <ActivityRow
                  key={row.activity.id}
                  activity={row.activity}
                  members={row.members}
                  showClockTime={day.kind !== "today"}
                  isLast={index === day.rows.length - 1}
                />
              ))}
            </section>
          ))
        )}
      </div>
    </div>
  );
};

export default RecentActivitySection;
